import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useCurtainStore } from '../../stores/curtain.store';
import { avviaKatana } from '../../lib/katana';
import type { LoginResponse } from '@masamune/shared';
import { Icon } from '../../components/ui/Icon';

// La soglia tra i due mondi: neutra a riposo, si tinge col colore-divisione sul focus
// (email = talent, password = product: i brand vivono solo nei dettagli).
const TINT = { idle: 'rgba(255,255,255,0.20)', email: '#42b8ff', password: '#6b25ff' } as const;

export function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const openCurtain = useCurtainStore((s) => s.open);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tint, setTint] = useState<keyof typeof TINT>('idle');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<LoginResponse>('/auth/login', { email, password }, { auth: false });
      setAuth(res.user, res.accessToken);
      // Uscita. Dal computer: il sipario (replica della login) si apre in due e
      // scopre l'app, che si monta SUBITO dietro. Dal telefono: lo sfodero
      // della katana (lib/katana.ts), lo stesso che parte quando si apre l'app
      // dalla home, perche' Lorenzo lo vuole a ogni ingresso. Con reduced-motion
      // niente di niente: si naviga e basta (katana.ts lo controlla da se').
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const band = window.matchMedia?.('(max-width: 860px)').matches;
      if (band) avviaKatana();
      else if (!reduce) openCurtain();
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Errore di rete');
      setLoading(false);
    }
  };

  return (
    <div className="login-split" style={{ ['--seam-tint' as any]: TINT[tint] }}>
      <div className="login-scene">
        {/* Lato brand: pannello viola profondo con aura + watermark M */}
        <aside className="login-brand">
          <img src="/logos/masamune.svg" alt="Masamune" className="login-brand-logo" />
          <div className="login-brand-foot">
            {/* In italiano e senza slogan (26/10/2026): prima "Built for the people building the future". Uguale nel sipario (LoginCurtain). */}
            <div className="login-payoff">Clienti, lavori e soldi di Masamune, <span className="login-payoff-em">in un posto solo</span>.</div>
            <div className="login-os">OS v0.1</div>
          </div>
        </aside>

        {/* Lato form: carta chiara, nuova UI */}
        <section className="login-form-side">
          <div className="login-form-col">
            <div className="login-form-head">
              <div className="kicker">Accesso riservato</div>
              <h1 className="login-form-title">Bentornato.</h1>
            </div>
            <form onSubmit={submit} className="login-form stagger">
              <div className="login-field">
                <label className="field-label">Email</label>
                <input
                  className="inp" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setTint('email')} onBlur={() => setTint('idle')}
                  required autoFocus
                />
              </div>
              <div className="login-field">
                <label className="field-label">Password</label>
                <input
                  className="inp" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setTint('password')} onBlur={() => setTint('idle')}
                  required
                />
              </div>
              {error && (
                <div className="login-err" key={error}>
                  <Icon name="alert" size={12} />{error}
                </div>
              )}
              <button type="submit" className="btn btn-primary w-full justify-center" disabled={loading}>
                {loading ? 'Accesso…' : <>Accedi <Icon name="arrowRight" size={14} /></>}
              </button>
            </form>
          </div>
        </section>

        {/* La soglia luminosa tra i due mondi (si tinge sul focus, si sguaina all'arrivo) */}
        <div className="login-seam" aria-hidden="true" />
      </div>
    </div>
  );
}
